import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where
} from "firebase/firestore";
import { Header } from "../../components/Header/Header";
import { Footer } from "../../components/Footer/Footer";
import { useAuth } from "../../../context/AuthContext";
import { db } from "../../../service/firebase";
import { deleteTurmaAndData, updateTurmaSettings } from "../../../service/turmaService";
import "./TurmaSettings.css";

const TurmaSettings = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [turma, setTurma] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [formData, setFormData] = useState({
    nome: "",
    descricao: "",
    regras: "",
    local: "",
    diasSemana: [],
    qtdJogadoresTime: "",
    tempoPartida: "",
    maxPontosPartida: "",
    maxVitoriasSeguidas: "",
    dadosIndividuais: false,
    corTime1: "#169e2b",
    corTime2: "#000000",
    status: 1,
    protegida: false,
    senhaGrupo: ""
  });
  const [deleting, setDeleting] = useState(false);

  const daysOfWeek = useMemo(
    () => [
      "Segunda-feira",
      "Terça-feira",
      "Quarta-feira",
      "Quinta-feira",
      "Sexta-feira",
      "Sábado",
      "Domingo"
    ],
    []
  );

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      setLoading(true);
      setError("");
      try {
        const turmaRef = doc(db, "turmas", id);
        const turmaSnapshot = await getDoc(turmaRef);
        if (!turmaSnapshot.exists()) {
          throw new Error("Turma não encontrada.");
        }
        const turmaData = turmaSnapshot.data();
        setTurma({ id, ...turmaData });
        setFormData({
          nome: turmaData.nome || "",
          descricao: turmaData.descricao || "",
          regras: turmaData.regras || "",
          local: turmaData.local || "",
          diasSemana: turmaData.dias_semana || [],
          qtdJogadoresTime: turmaData.qtd_jogadores_time?.toString() || "",
          tempoPartida: turmaData.tempo_de_partida?.toString() || "",
          maxPontosPartida: turmaData.max_pontos_partida?.toString() || "",
          maxVitoriasSeguidas: turmaData.max_vitorias?.toString() || "0",
          dadosIndividuais: Boolean(turmaData.dados_individuais),
          corTime1: turmaData.cor_time_1 || "#169e2b",
          corTime2: turmaData.cor_time_2 || "#000000",
          status: typeof turmaData.status === "number" ? turmaData.status : 1,
          protegida: Boolean(turmaData.protegida),
          senhaGrupo: turmaData.senha_grupo || ""
        });

        const membershipSnapshot = await getDocs(
          query(
            collection(db, "jogador_turma"),
            where("id_turma", "==", id),
            where("id_usuario", "==", user.uid)
          )
        );
        const membership = membershipSnapshot.docs[0]?.data();
        const adminStatus = Boolean(membership?.admin);
        setIsAdmin(adminStatus);
        if (!adminStatus) {
          setError("Você não tem permissão para alterar esta turma.");
        }
      } catch (loadError) {
        console.error("Erro ao carregar configurações da turma", loadError);
        setError(loadError.message || "Não foi possível carregar as configurações agora.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, user]);

  const handleBack = () => {
    navigate(`/turmas/${id}`);
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const toggleDay = (day) => {
    setFormData((current) => {
      const alreadySelected = current.diasSemana.includes(day);
      return {
        ...current,
        diasSemana: alreadySelected
          ? current.diasSemana.filter((item) => item !== day)
          : [...current.diasSemana, day]
      };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isAdmin) return;
    if (
      !formData.nome ||
      !formData.descricao ||
      !formData.regras ||
      !formData.local ||
      !formData.qtdJogadoresTime ||
      !formData.tempoPartida ||
      !formData.maxPontosPartida ||
      formData.diasSemana.length === 0
    ) {
      setError("Preencha todos os campos obrigatórios antes de salvar.");
      return;
    }
    if (formData.protegida && !formData.senhaGrupo) {
      setError("Defina uma senha para turmas protegidas.");
      return;
    }
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await updateTurmaSettings({
        turmaId: id,
        data: {
          nome: formData.nome,
          descricao: formData.descricao,
          regras: formData.regras,
          dias_semana: formData.diasSemana,
          local: formData.local,
          qtd_jogadores_time: parseInt(formData.qtdJogadoresTime, 10),
          tempo_de_partida: parseInt(formData.tempoPartida, 10),
          max_pontos_partida: parseInt(formData.maxPontosPartida, 10),
          max_vitorias: parseInt(formData.maxVitoriasSeguidas || "0", 10) || 0,
          dados_individuais: formData.dadosIndividuais,
          cor_time_1: formData.corTime1,
          cor_time_2: formData.corTime2,
          status: Number(formData.status),
          protegida: formData.protegida,
          senha_grupo: formData.protegida ? formData.senhaGrupo : ""
        }
      });
      setSuccess("Configurações atualizadas com sucesso.");
      setTurma((current) =>
        current
          ? {
              ...current,
              nome: formData.nome,
              descricao: formData.descricao,
              regras: formData.regras,
              dias_semana: formData.diasSemana,
              local: formData.local,
              qtd_jogadores_time: parseInt(formData.qtdJogadoresTime, 10),
              tempo_de_partida: parseInt(formData.tempoPartida, 10),
              max_pontos_partida: parseInt(formData.maxPontosPartida, 10),
              max_vitorias: parseInt(formData.maxVitoriasSeguidas || "0", 10) || 0,
              dados_individuais: formData.dadosIndividuais,
              cor_time_1: formData.corTime1,
              cor_time_2: formData.corTime2,
              status: Number(formData.status),
              protegida: formData.protegida,
              senha_grupo: formData.protegida ? formData.senhaGrupo : ""
            }
          : current
      );
    } catch (updateError) {
      console.error("Erro ao atualizar configurações da turma", updateError);
      setError(updateError.message || "Não foi possível salvar as configurações.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTurma = async () => {
    if (!isAdmin || deleting) return;
    const confirmed = window.confirm(
      "Tem certeza de que deseja excluir esta turma? Todos os dados serão removidos permanentemente."
    );
    if (!confirmed) {
      return;
    }
    setDeleting(true);
    setError("");
    setSuccess("");
    try {
      await deleteTurmaAndData(id);
      navigate("/turmas");
    } catch (deleteError) {
      console.error("Erro ao excluir turma", deleteError);
      setError(deleteError.message || "Não foi possível excluir a turma.");
    } finally {
      setDeleting(false);
    }
  };

  const disableForm = loading || !isAdmin || deleting;
  const selectedDays = formData.diasSemana;

  return (
    <>
      <Header />
      <div className="body">
        <div className="center">
          <div className="body-content card turma-settings-card">
            <div className="topo-page">
              <strong># Configurações da Turma</strong>
            </div>
            {loading ? (
              <p>Carregando configurações...</p>
            ) : error && !isAdmin ? (
              <>
                <p className="turma-settings-error" role="alert">
                  {error}
                </p>
                <button type="button" className="btn-default" onClick={handleBack}>
                  Voltar
                </button>
              </>
            ) : (
              <form className="turma-settings-form" onSubmit={handleSubmit}>
                {turma && (
                  <div className="turma-settings-summary">
                    <h3>{turma.nome}</h3>
                    <p>{turma.descricao}</p>
                  </div>
                )}
                {error && isAdmin && (
                  <p className="turma-settings-error" role="alert">
                    {error}
                  </p>
                )}
                {success && (
                  <p className="turma-settings-success">{success}</p>
                )}
                <label htmlFor="nome">Nome</label>
                <input
                  id="nome"
                  name="nome"
                  type="text"
                  value={formData.nome}
                  onChange={handleInputChange}
                  disabled={disableForm}
                />

                <label htmlFor="descricao">Descrição</label>
                <textarea
                  id="descricao"
                  name="descricao"
                  value={formData.descricao}
                  onChange={handleInputChange}
                  disabled={disableForm}
                />

                <label htmlFor="regras">Regras</label>
                <textarea
                  id="regras"
                  name="regras"
                  value={formData.regras}
                  onChange={handleInputChange}
                  disabled={disableForm}
                />

                <label>Dias da semana</label>
                <div className="days-grid">
                  {daysOfWeek.map((day) => (
                    <label key={day} className="day-option">
                      <input
                        type="checkbox"
                        checked={selectedDays.includes(day)}
                        onChange={() => !disableForm && toggleDay(day)}
                        disabled={disableForm}
                      />
                      <span>{day}</span>
                    </label>
                  ))}
                </div>

                <label htmlFor="local">Local</label>
                <input
                  id="local"
                  name="local"
                  type="text"
                  value={formData.local}
                  onChange={handleInputChange}
                  disabled={disableForm}
                />

                <label htmlFor="qtdJogadoresTime">Qtd. jogadores por time</label>
                <input
                  id="qtdJogadoresTime"
                  name="qtdJogadoresTime"
                  type="number"
                  min="1"
                  value={formData.qtdJogadoresTime}
                  onChange={handleInputChange}
                  disabled={disableForm}
                />

                <label htmlFor="tempoPartida">Tempo de partida (min)</label>
                <input
                  id="tempoPartida"
                  name="tempoPartida"
                  type="number"
                  min="1"
                  value={formData.tempoPartida}
                  onChange={handleInputChange}
                  disabled={disableForm}
                />

                <label htmlFor="maxPontosPartida">Máx. pontos por partida</label>
                <input
                  id="maxPontosPartida"
                  name="maxPontosPartida"
                  type="number"
                  min="1"
                  value={formData.maxPontosPartida}
                  onChange={handleInputChange}
                  disabled={disableForm}
                />

                <label htmlFor="maxVitoriasSeguidas">Máx. vitórias seguidas</label>
                <input
                  id="maxVitoriasSeguidas"
                  name="maxVitoriasSeguidas"
                  type="number"
                  min="0"
                  value={formData.maxVitoriasSeguidas}
                  onChange={handleInputChange}
                  disabled={disableForm}
                />

                <label className="toggle-field">
                  <input
                    type="checkbox"
                    name="dadosIndividuais"
                    checked={formData.dadosIndividuais}
                    onChange={handleInputChange}
                    disabled={disableForm}
                  />
                  Registrar dados individuais
                </label>

                <div className="colors-grid">
                  <label htmlFor="corTime1">Cor Time 1</label>
                  <input
                    id="corTime1"
                    name="corTime1"
                    type="color"
                    className="input-color"
                    value={formData.corTime1}
                    onChange={handleInputChange}
                    disabled={disableForm}
                  />
                  <label htmlFor="corTime2">Cor Time 2</label>
                  <input
                    id="corTime2"
                    name="corTime2"
                    type="color"
                    className="input-color"
                    value={formData.corTime2}
                    onChange={handleInputChange}
                    disabled={disableForm}
                  />
                </div>

                <label htmlFor="status">Status da turma</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  disabled={disableForm}
                >
                  <option value={1}>Ativa</option>
                  <option value={0}>Inativa</option>
                  <option value={2}>Encerrada</option>
                </select>

                <label className="toggle-field">
                  <input
                    type="checkbox"
                    name="protegida"
                    checked={formData.protegida}
                    onChange={handleInputChange}
                    disabled={disableForm}
                  />
                  Exigir senha para novos membros
                </label>
                {formData.protegida && (
                  <>
                    <label htmlFor="turma-senha">Senha da turma</label>
                    <input
                      id="turma-senha"
                      type="password"
                      name="senhaGrupo"
                      value={formData.senhaGrupo}
                      onChange={handleInputChange}
                      disabled={disableForm}
                      placeholder="Digite a senha da turma"
                    />
                  </>
                )}
                <div className="turma-settings-actions">
                  <button type="button" className="btn-outline" onClick={handleBack}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn-default" disabled={disableForm || saving}>
                    {saving ? "Salvando..." : "Salvar configurações"}
                  </button>
                </div>
                <div className="turma-settings-danger">
                  <p>
                    Deseja excluir esta turma? Todos os jogos, convites e integrantes associados serão removidos
                    permanentemente.
                  </p>
                  <button
                    type="button"
                    className="btn-danger"
                    onClick={handleDeleteTurma}
                    disabled={deleting || saving}
                  >
                    {deleting ? "Excluindo..." : "Excluir turma"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default TurmaSettings;
